import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  InputOrderData,
  OrderSectionErrors,
  OrderTypes,
  RemoveOrder,
  SetOrders,
  UpdateOrder,
} from "@shared/features/trades/common/types";
import {
  DEFAULT_BREAKOUT_ENTRY_NUM_ORDERS,
  DEFAULT_BREAKOUT_ENTRY_PRICE_RANGE_DISTANCE,
  DEFAULT_BREAKOUT_TAKE_PROFIT_PRICE_RANGE_DISTANCE,
  DEFAULT_ENTRY_PRICE_RANGE_DISTANCE,
  DEFAULT_NUM_ORDERS,
  DEFAULT_TAKE_PROFIT_NUM_ORDERS,
  DEFAULT_TAKE_PROFIT_PRICE_RANGE_DISTANCE,
  ORDER_TYPE_TO_CREATE_TRADE_TAB,
  ORDER_TYPE_TO_STRATEGY_ITEM_NAME,
  TRADE_DEFAULT_STOP,
} from "@shared/features/tradingTerminal/common/constants";
import {
  NUMBER_OF_ORDERS_ITEM_NAME,
  PRICE_RANGE_ITEM_NAME,
} from "@shared/features/configuration/constants";
import {
  getItemByName,
  setItemValue,
} from "@shared/features/configuration/utils";
import {
  getNormalizedPercentages,
  getOrdersDefaultSortOrder,
} from "@shared/features/trades/common/utils";
import { BaseConfigurationItem, SortingOrder } from "@shared/types";
import { findLastNonZeroValue } from "@shared/utils/arrays";
import { cloneDeep, isEqual } from "lodash";
import {
  GeneralConfigurationData,
  PriceRange,
  TradeTypes,
} from "@shared/features/tradingTerminal/common/types";
import {
  buildOrders,
  getDistributedTargets,
  getNumOrdersValidationMessage,
  getStrategyOrdersPercentages,
} from "@shared/features/tradingTerminal/common/utils";
import { toFixed } from "@shared/utils/numbers";
import { getDefaultOrderPrice } from "@shared/features/bots/BotForm/common/utils";

interface useOrdersSectionProps {
  orderType: OrderTypes;
  orders: InputOrderData[];
  setOrders: SetOrders;
  generalConfigurationData: GeneralConfigurationData;
  baselineEntryPrice: number;
  ordersErrors?: OrderSectionErrors;
  useOrders?: boolean;
  setUseOrders?: (useOrders: boolean) => void;
}

const useOrdersSection = ({
  orderType,
  orders,
  setOrders,
  generalConfigurationData,
  baselineEntryPrice,
  ordersErrors,
  useOrders,
  setUseOrders,
}: useOrdersSectionProps) => {
  const {
    precisionConfig,
    amount,
    amountInitialized,
    extraInfo,
    shouldRedistributePriceTargets,
    setShouldRedistributePriceTargets,
    priceTargetsModified,
    setPriceTargetsModified,
    strategyValuesModified,
    setStrategyValuesModified,
    setRatioTargets,
    setShowRedistributeTargetsButton,
    defaultStop,
    setDefaultStop,
    getCreateGeneralDataQuery,
    symbols,
    priceRangeInitialized,
    setPriceRangeInitialized,
    account,
    exchanges,
    setTabWithErrors,
    currentPrice,
    shouldUpdatePriceRange,
    setShouldUpdatePriceRange,
    shouldUpdateNumOfOrders,
    setShouldUpdateNumOfOrders,
    useFreeTextData,
    freeTextProcessed,
    updatedItemsToFreeTextData,
    setUpdatedItemsToFreeTextData,
    isTpNumOrdersInitialized,
    setIsTpNumOrdersInitialized,
    setShowNewCustomStrategy,
    isLoading,
    isFetching,
    strategyHasFixedNumOrders,
    setStrategyHasFixedNumOrders,
    valuesModified,
    setValuesModified,
    ordersInitialized,
    setOrdersInitialized,
    numOrdersChangedManually,
    setNumOrdersChangedManually,
    stopDistanceInputModified,
    stopBaselineModified,
    useTradingTerminalOldVersion,
    isStrategyInitialized,
    setIsStrategyInitialized,
    isSignal,
    ignoreOrdersRatios,
  } = useContext(TradingTerminalContext)!;

  const {
    generalConfiguration,
    strategy,
    strategyId,
    numOrders,
    priceRange,
    itemsModified,
    setItemsModified,
    clonedConfiguration,
    updateConfiguration,
    configurationModified,
  }: GeneralConfigurationData = generalConfigurationData || {};

  const {
    currentData: createTradeGeneralData,
    isFetching: createTradeGeneralDataFetching,
  } = getCreateGeneralDataQuery;

  const [prevNumOrders, setPrevNumOrders] = useState<number>();
  const [ordersUpdated, setOrdersUpdated] = useState(false);
  const showEmptyState = !createTradeGeneralData;
  const { limits } = createTradeGeneralData || {};
  const isShort = !!extraInfo?.reverse;
  const isBreakout = extraInfo?.trade_type === TradeTypes.BREAKOUT;
  const ordersDisabled =
    !createTradeGeneralDataFetching && !!setUseOrders && !useOrders;
  const isStop = orderType === OrderTypes.STOP;
  const isEntry = orderType === OrderTypes.ENTRY;
  const isTakeProfit = orderType === OrderTypes.TAKE_PROFIT;
  const sortOrder = useMemo(
    () => getOrdersDefaultSortOrder(orderType, isShort, isBreakout),
    [orderType, isShort, isBreakout]
  );
  const ignoreRatios = ignoreOrdersRatios[orderType];

  const strategyItem = getItemByName(
    ORDER_TYPE_TO_STRATEGY_ITEM_NAME[orderType],
    clonedConfiguration
  );
  const numOrdersItem = getItemByName(
    NUMBER_OF_ORDERS_ITEM_NAME,
    clonedConfiguration
  );
  const priceRangeItem = getItemByName(
    PRICE_RANGE_ITEM_NAME,
    clonedConfiguration
  );

  const strategySelectChanged = itemsModified?.includes(strategyItem?.uuid);
  const numOrdersSelectChanged = itemsModified?.includes(numOrdersItem?.uuid);
  const priceRangeInputChanged = itemsModified?.includes(priceRangeItem?.uuid);

  const deps = [orders, sortOrder, numOrders, numOrdersSelectChanged];

  const priceTargets = useMemo(() => {
    const prices = orders.map((order) => Number(order.price));
    if (
      numOrdersSelectChanged &&
      (priceTargetsModified[orderType] || numOrdersChangedManually)
    ) {
      while (numOrders > prices.length) {
        prices.push(
          sortOrder === SortingOrder.ASCEND ? priceRange?.max : priceRange?.min
        );
      }
    }
    return prices;
  }, [
    ...deps,
    priceTargetsModified[orderType],
    priceRange?.min,
    priceRange?.max,
  ]);

  let ratioTargets = useMemo(() => {
    let ordersToUse = orders.slice();
    if (shouldRedistributePriceTargets[orderType]) {
      ordersToUse = ordersToUse.sort(
        (a, b) => sortOrder * (Number(a.price) - Number(b.price))
      );
    }
    let percentTargets = ordersToUse.map((order) => Number(order.percent));
    if (numOrdersSelectChanged && strategyValuesModified[orderType]) {
      while (numOrders > percentTargets.length) {
        percentTargets.push(0);
      }
    }
    return percentTargets;
  }, [
    ...deps,
    shouldRedistributePriceTargets[orderType],
    strategyValuesModified[orderType],
  ]);

  useEffect(() => {
    if (!isLoading && ordersInitialized[orderType] && amountInitialized) {
      setTabWithErrors(
        ORDER_TYPE_TO_CREATE_TRADE_TAB[orderType],
        Object.keys(ordersErrors?.section || {}).length > 0
      );
    }
  }, [
    isLoading,
    orderType,
    ordersInitialized[orderType],
    Object.keys(ordersErrors?.section || {}).length,
    amountInitialized,
  ]);

  useEffect(() => {
    if (configurationModified) {
      setValuesModified(true);
    }
  }, [configurationModified]);

  useEffect(() => {
    if (!ordersInitialized[orderType]) {
      setItemsModified([]);
    }
  }, [configurationModified, ordersInitialized[orderType]]);

  useEffect(() => {
    if (numOrdersItem) {
      const shouldDisplayItem: BaseConfigurationItem<boolean>[] =
        numOrdersItem?.should_display;
      const shouldDisplayStrategyIdsArray = shouldDisplayItem?.find(
        (option) => option.outcome
      )?.constraints[0]?.values;

      if (strategyId && !shouldDisplayStrategyIdsArray?.includes(strategyId))
        shouldDisplayStrategyIdsArray?.push(strategyId);
    }
  }, [strategyId, numOrdersItem]);

  const checkIfPricesDistributed = useCallback(
    (
      priceRange: PriceRange,
      propsNumOrders?: number,
      priceTargets?: number[]
    ) => {
      const distributedPriceTargets = getDistributedTargets(
        orderType,
        propsNumOrders ?? numOrders,
        isShort,
        isBreakout,
        priceRange
      ).map((price) => toFixed(price, precisionConfig?.price));
      setShowRedistributeTargetsButton(
        orderType,
        !isEqual(distributedPriceTargets, priceTargets)
      );
    },
    [
      orderType,
      numOrders,
      isShort,
      isBreakout,
      priceTargets,
      precisionConfig?.price,
    ]
  );

  const updateFreeTextOrders =
    useFreeTextData &&
    ordersInitialized[orderType] &&
    (configurationModified || shouldRedistributePriceTargets[orderType]);
  const useCurrentPriceTargets =
    !shouldRedistributePriceTargets[orderType] &&
    (priceTargetsModified[orderType] ||
      (useFreeTextData && !updateFreeTextOrders) ||
      numOrdersChangedManually[orderType]);

  const getOrdersData = useCallback(() => {
    return buildOrders(
      numOrders,
      strategy || [],
      Number(amount) || 0,
      precisionConfig?.percent || 0,
      precisionConfig?.price || 0,
      precisionConfig?.amount || 0,
      orderType,
      baselineEntryPrice,
      isShort,
      isBreakout,
      priceRange,
      useCurrentPriceTargets ? priceTargets : undefined,
      strategyValuesModified[orderType] ? ratioTargets : undefined,
      isSignal
    );
  }, [
    numOrders,
    strategy,
    amount,
    precisionConfig?.percent,
    precisionConfig?.price,
    precisionConfig?.amount,
    orderType,
    baselineEntryPrice,
    isShort,
    isBreakout,
    priceRange.min,
    priceRange.max,
    useCurrentPriceTargets,
    priceTargets,
    strategyValuesModified[orderType],
    ratioTargets,
  ]);

  useEffect(() => {
    if (
      !isStop &&
      (!useFreeTextData || (freeTextProcessed && updateFreeTextOrders))
    ) {
      const updatedOrders = getOrdersData();
      if (!isEqual(updatedOrders, orders)) {
        updateOrders(updatedOrders);
        checkIfPricesDistributed(
          priceRange,
          updatedOrders?.length,
          updatedOrders.map((order) => Number(order.price))
        );
        setItemsModified([]);
        if (
          isStrategyInitialized[orderType] &&
          priceRangeInitialized[orderType]
        ) {
          setOrdersInitialized(orderType, true);
        }
        if (shouldRedistributePriceTargets?.[orderType]) {
          setPriceTargetsModified(orderType, false);
          setShouldRedistributePriceTargets(orderType, false);
        }
      }
    }
  }, [
    orderType,
    useFreeTextData,
    freeTextProcessed,
    updateFreeTextOrders,
    getOrdersData,
    priceRange.min,
    priceRange.max,
    isStrategyInitialized[orderType],
    priceRangeInitialized[orderType],
    amountInitialized,
    shouldRedistributePriceTargets?.[orderType],
    checkIfPricesDistributed,
  ]);

  useEffect(() => {
    if (priceRangeInputChanged) setPriceTargetsModified(orderType, false);
  }, [priceRangeInputChanged]);

  useEffect(() => {
    if (numOrdersSelectChanged || priceRangeInputChanged) {
      setNumOrdersChangedManually(orderType, false);
    }
  }, [numOrdersSelectChanged, priceRangeInputChanged]);

  useEffect(() => {
    if (!isStrategyInitialized[orderType] || strategySelectChanged) {
      setStrategyHasFixedNumOrders(orderType, false);
      setStrategyValuesModified(orderType, false);
      setShowNewCustomStrategy(orderType, false);
      setRatioTargets(orderType, undefined);
      setNumOrdersChangedManually(orderType, false);

      if (strategy) {
        const numOfOrders = findLastNonZeroValue(strategy);
        if (numOfOrders < strategy.length) {
          setStrategyHasFixedNumOrders(orderType, true);
          setItemValue(numOrdersItem, numOfOrders);
          updateConfiguration();
        }
        setItemsModified([]);
        if (!isStrategyInitialized[orderType])
          setIsStrategyInitialized(orderType, true);
      }
    }
  }, [
    isStrategyInitialized[orderType],
    strategySelectChanged,
    strategy,
    orderType,
  ]);

  useEffect(() => {
    if (
      strategyValuesModified[orderType] &&
      !strategySelectChanged &&
      ordersUpdated
    ) {
      const originalStrategyPercentages = getStrategyOrdersPercentages(
        strategy,
        numOrders,
        precisionConfig?.percent || 0
      );
      const isEqualToOriginal = isEqual(
        originalStrategyPercentages,
        ratioTargets
      );
      setShowNewCustomStrategy(orderType, !isEqualToOriginal);
      if (isEqualToOriginal) {
        setStrategyValuesModified(orderType, false);
      }
      setOrdersUpdated(false);
    }
  }, [
    strategyValuesModified[orderType],
    strategySelectChanged,
    strategy,
    numOrders,
    precisionConfig?.percent,
    ratioTargets,
    orderType,
    ordersUpdated,
  ]);

  useEffect(() => {
    if (numOrdersSelectChanged && strategyHasFixedNumOrders[orderType]) {
      setStrategyValuesModified(orderType, true);
    }
  }, [numOrdersSelectChanged, strategyHasFixedNumOrders[orderType]]);

  useEffect(() => {
    if (strategyValuesModified[orderType]) {
      setRatioTargets(orderType, ratioTargets);
    }
  }, [strategyValuesModified[orderType], ratioTargets]);

  useEffect(() => {
    if (
      isTakeProfit &&
      !isTpNumOrdersInitialized &&
      isStrategyInitialized[orderType] &&
      numOrdersItem &&
      !useFreeTextData
    ) {
      if (!strategyHasFixedNumOrders[orderType]) {
        setItemValue(numOrdersItem, DEFAULT_TAKE_PROFIT_NUM_ORDERS);
        updateConfiguration();
      }
      setIsTpNumOrdersInitialized(true);
    }
  }, [
    isTakeProfit,
    orderType,
    isTpNumOrdersInitialized,
    isStrategyInitialized[orderType],
    useFreeTextData,
    strategyHasFixedNumOrders[orderType],
  ]);

  useEffect(() => {
    if (
      isEntry &&
      shouldUpdateNumOfOrders &&
      !priceTargetsModified[orderType] &&
      !useFreeTextData
    ) {
      if (!strategyHasFixedNumOrders[orderType] && !isBreakout) {
        setItemValue(numOrdersItem, DEFAULT_NUM_ORDERS);
      } else if (isBreakout && numOrders > DEFAULT_BREAKOUT_ENTRY_NUM_ORDERS) {
        setItemValue(numOrdersItem, DEFAULT_BREAKOUT_ENTRY_NUM_ORDERS);
      }
      setShouldUpdateNumOfOrders(false);
    }
  }, [
    orderType,
    shouldUpdateNumOfOrders,
    priceTargetsModified[orderType],
    isBreakout,
    useFreeTextData,
    strategyHasFixedNumOrders[orderType],
    numOrders,
  ]);

  const getPriceRangeLimit = useCallback(
    (distance: number) => {
      const factor = 1 + sortOrder * (distance / 100);
      if (currentPrice)
        return toFixed(currentPrice * factor, precisionConfig?.price);
    },
    [currentPrice, sortOrder, precisionConfig?.price]
  );

  const getDefaultPriceRange = useCallback(() => {
    let defaultPriceRangeDistance = [0, 0];
    if (isEntry) {
      defaultPriceRangeDistance = cloneDeep(
        isBreakout
          ? DEFAULT_BREAKOUT_ENTRY_PRICE_RANGE_DISTANCE
          : DEFAULT_ENTRY_PRICE_RANGE_DISTANCE
      );
    } else if (isTakeProfit) {
      defaultPriceRangeDistance = cloneDeep(
        isBreakout
          ? DEFAULT_BREAKOUT_TAKE_PROFIT_PRICE_RANGE_DISTANCE
          : DEFAULT_TAKE_PROFIT_PRICE_RANGE_DISTANCE
      );
    }
    if (sortOrder === SortingOrder.DESCEND) {
      defaultPriceRangeDistance = defaultPriceRangeDistance.reverse();
    }

    return {
      min: getPriceRangeLimit(defaultPriceRangeDistance[0]),
      max: getPriceRangeLimit(defaultPriceRangeDistance[numOrders > 1 ? 1 : 0]),
    };
  }, [
    isEntry,
    isTakeProfit,
    orderType,
    isBreakout,
    isShort,
    sortOrder,
    numOrders,
    getPriceRangeLimit,
  ]);

  useEffect(() => {
    if (
      (!priceRangeInitialized[orderType] ||
        shouldUpdatePriceRange[orderType]) &&
      priceRangeItem &&
      !useFreeTextData &&
      isStrategyInitialized[orderType] &&
      (isEntry || (isTakeProfit && isTpNumOrdersInitialized))
    ) {
      setItemValue(priceRangeItem, getDefaultPriceRange());
      updateConfiguration();
      setPriceRangeInitialized(orderType, true);
      setShouldUpdatePriceRange(orderType, false);
    }
  }, [
    priceRangeInitialized[orderType],
    shouldUpdatePriceRange[orderType],
    orderType,
    priceRangeItem,
    useFreeTextData,
    getDefaultPriceRange,
    isStrategyInitialized[orderType],
    isTpNumOrdersInitialized,
  ]);

  useEffect(() => {
    if (!createTradeGeneralDataFetching && !!createTradeGeneralData) {
      setPriceRangeInitialized(orderType, false);
      setOrdersInitialized(orderType, false);
      setIsTpNumOrdersInitialized(false);
      setIsStrategyInitialized(orderType, false);
    }
  }, [
    createTradeGeneralDataFetching,
    createTradeGeneralData,
    symbols?.[0],
    account?.client_id,
    exchanges,
    orderType,
  ]);

  const defaultStopPrice = useMemo(() => {
    if (ordersInitialized[OrderTypes.ENTRY]) {
      return getDefaultOrderPrice(
        defaultStop ?? TRADE_DEFAULT_STOP,
        undefined,
        extraInfo?.reverse ?? false,
        OrderTypes.STOP,
        undefined,
        baselineEntryPrice
      );
    }
  }, [
    defaultStop.baseline,
    defaultStop.percent,
    extraInfo?.reverse,
    ordersInitialized[OrderTypes.ENTRY],
    baselineEntryPrice,
  ]);

  const shouldUpdateDefaultStopToFreeText =
    !updatedItemsToFreeTextData[orderType] &&
    !!orders?.[0]?.distance &&
    !ordersInitialized[orderType] &&
    ordersInitialized[OrderTypes.ENTRY];
  const updateDefaultStop =
    priceTargetsModified[orderType] || shouldUpdateDefaultStopToFreeText;
  const updateStopOrder =
    !!defaultStopPrice &&
    (!useFreeTextData ||
      stopDistanceInputModified ||
      stopBaselineModified ||
      !useOrders);

  useEffect(() => {
    if (orderType === OrderTypes.STOP) {
      if (updateDefaultStop) {
        setDefaultStop((prevDefaultStop) => ({
          ...prevDefaultStop,
          percent: orders?.[0]?.distance || "N/A",
        }));
        if (!ordersInitialized[orderType])
          setOrdersInitialized(orderType, true);
        if (!updatedItemsToFreeTextData[orderType])
          setUpdatedItemsToFreeTextData(orderType, true);
      } else if (updateStopOrder) {
        const order = {
          price: toFixed(defaultStopPrice, precisionConfig?.price)?.toString(),
          percent: "100",
          distance: defaultStop?.percent?.toString(),
        };
        setOrders([order]);
        if (!ordersInitialized[orderType])
          setOrdersInitialized(orderType, true);
      }
    }
  }, [
    orderType,
    defaultStopPrice,
    defaultStop?.percent,
    defaultStop?.baseline,
    orders?.[0]?.distance,
    updateDefaultStop,
    updateStopOrder,
    ordersInitialized[orderType],
    updatedItemsToFreeTextData[orderType],
    precisionConfig?.price,
  ]);

  const setPriceRangeItemValue = useCallback(
    (prices: number[]) => {
      const min = prices.length ? Math.min(...prices) : currentPrice;
      const max = prices.length ? Math.max(...prices) : currentPrice;
      setItemValue(priceRangeItem, { min, max });
      updateConfiguration();
      if (!!min && !!max)
        checkIfPricesDistributed({ min, max }, prices.length, prices);
    },
    [checkIfPricesDistributed]
  );

  useEffect(() => {
    if (priceTargetsModified[orderType] && !priceRangeInputChanged) {
      setPriceRangeItemValue(priceTargets);
    }
  }, [
    priceTargetsModified[orderType],
    priceRangeInputChanged,
    priceTargets,
    setPriceRangeItemValue,
  ]);

  useEffect(() => {
    if (
      !updatedItemsToFreeTextData[orderType] &&
      isStrategyInitialized[orderType] &&
      ((isTakeProfit && ordersInitialized[OrderTypes.ENTRY]) || isEntry)
    ) {
      setItemValue(numOrdersItem, orders.length);
      setPriceRangeItemValue(priceTargets);
      updateConfiguration();
      setUpdatedItemsToFreeTextData(orderType, true);
      setItemsModified([]);
      setOrdersInitialized(orderType, true);
    }
  }, [
    updatedItemsToFreeTextData[orderType],
    orders.length,
    priceTargets,
    setPriceRangeItemValue,
    orderType,
    ordersInitialized[OrderTypes.ENTRY],
    isStrategyInitialized[orderType],
  ]);

  useEffect(() => {
    if (
      !isStop &&
      prevNumOrders !== numOrders &&
      priceTargets.length === numOrders &&
      ordersInitialized[orderType] &&
      !numOrdersSelectChanged
    ) {
      setPriceRangeItemValue(priceTargets);
      setPrevNumOrders(numOrders);
    }
  }, [
    prevNumOrders,
    numOrders,
    priceTargets,
    ordersInitialized[orderType],
    numOrdersSelectChanged,
    setPriceRangeItemValue,
  ]);

  const updateOrders = (orders: InputOrderData[]) => {
    setOrders(orders);
    updateConfiguration?.();
  };

  const updateOrder: UpdateOrder = (
    index: number,
    _orderType: OrderTypes,
    updatedOrder: InputOrderData
  ) => {
    const updatedOrders = orders.slice();
    updatedOrders[index] = updatedOrder;
    updateOrders(updatedOrders);
    setOrdersUpdated(true);
  };

  const removeOrder: RemoveOrder = (
    orderType: OrderTypes,
    indexToRemove: number
  ) => {
    setNumOrdersChangedManually(orderType, true);
    const updatedOrders = orders.filter((_, index) => index !== indexToRemove);
    updateOrders(updatedOrders);
    setItemValue(numOrdersItem, numOrders > 0 ? numOrders - 1 : 0);
    if (strategyHasFixedNumOrders[orderType])
      setStrategyValuesModified(orderType, true);
    setValuesModified(true);
  };

  const addOrder = () => {
    setNumOrdersChangedManually(orderType, true);
    const newPrice =
      sortOrder === SortingOrder.ASCEND ? priceRange?.max : priceRange?.min;
    const newOrder: InputOrderData = {
      price: (newPrice || 0).toString() || "",
      percent: "",
    };
    if (isEntry) {
      newOrder.amount = "";
    }
    updateOrders([...orders, newOrder]);
    setItemValue(numOrdersItem, numOrders + 1);
    if (strategyHasFixedNumOrders[orderType]) {
      setStrategyValuesModified(orderType, true);
    }
    setValuesModified(true);
  };

  const resolveIssues = () => {
    const normalizedPercentages = getNormalizedPercentages(
      orders.map((order) => parseFloat(order.percent)),
      precisionConfig?.percent || 0
    );
    const resolvedOrders = orders.map((orderData, index) => {
      const percent = normalizedPercentages[index];
      let order = {
        ...orderData,
        percent: toFixed(percent, precisionConfig?.percent).toString(),
      };
      if (isEntry) {
        order = {
          ...order,
          amount: toFixed(
            (percent / 100) * Number(amount),
            precisionConfig?.amount || 0
          ).toString(),
        };
      }
      return order;
    });
    setOrders(resolvedOrders);
  };

  const onSwitchClick = () => {
    setUseOrders?.(!useOrders);
    if (!valuesModified) setValuesModified(true);
  };

  const addOrderButtonDisabledMessage = useMemo(
    () =>
      !isStop
        ? getNumOrdersValidationMessage(
            orders.length,
            orderType,
            isBreakout,
            limits
          )
        : undefined,
    [orders.length, orderType, isBreakout, limits]
  );

  return {
    generalConfiguration,
    numOrders,
    addOrder,
    removeOrder,
    updateOrder,
    resolveIssues,
    isLoading,
    isFetching,
    ordersDisabled,
    isStop,
    onSwitchClick,
    showEmptyState,
    addOrderButtonDisabledMessage,
    useTradingTerminalOldVersion,
    ignoreRatios,
  };
};

export default useOrdersSection;
